/**
 * Chat store (Zustand) — conversation state + streaming + widgets.
 */
import { create } from "zustand";

import type { Conversation, Message, Thought, Uuid, WidgetType } from "@/types";

export type ConnectionStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "closed";

export type WidgetStatus = "pending" | "submitted" | "approved" | "rejected";

export interface ActiveWidget {
  message_id: Uuid;
  widget_id: Uuid;
  widget_type: WidgetType;
  payload: Record<string, unknown>;
  status: WidgetStatus;
}

interface ChatState {
  conversations: Conversation[];
  activeConversationId: Uuid | null;
  messages: Message[];
  thoughts: Thought[];
  widgets: Map<string, ActiveWidget>;
  connectionStatus: ConnectionStatus;

  // LLM provider selection
  selectedProvider: string | null;
  selectedModel: string | null;

  /** Prefill draft (P2.1-S6 SuggestedPrompts). Composer reads + clears after consume. */
  composerDraft: string;
  setComposerDraft: (text: string) => void;

  setConversations: (items: Conversation[]) => void;
  setActiveConversation: (id: Uuid | null) => void;
  setMessages: (items: Message[]) => void;
  appendMessage: (msg: Message) => void;
  appendDelta: (messageId: Uuid, delta: string) => void;
  finishMessage: (messageId: Uuid) => void;
  addThought: (thought: Thought) => void;
  finishThought: (id: string) => void;
  setConnectionStatus: (status: ConnectionStatus) => void;
  addWidget: (widget: ActiveWidget) => void;
  updateWidgetStatus: (widget_id: Uuid, status: WidgetStatus) => void;
  setLLM: (provider: string | null, model: string | null) => void;
  reset: () => void;
}

const initialState = {
  conversations: [] as Conversation[],
  activeConversationId: null,
  messages: [] as Message[],
  thoughts: [] as Thought[],
  widgets: new Map<string, ActiveWidget>(),
  connectionStatus: "idle" as ConnectionStatus,
  selectedProvider: null as string | null,
  selectedModel: null as string | null,
  composerDraft: "" as string,
};

export const useChatStore = create<ChatState>((set) => ({
  ...initialState,

  setConversations: (items) => set({ conversations: items }),
  setActiveConversation: (id) =>
    set({
      activeConversationId: id,
      messages: [],
      thoughts: [],
      widgets: new Map(),
    }),
  setMessages: (items) => set({ messages: items }),
  appendMessage: (msg) =>
    set((s) => {
      // Khi user gửi tin nhắn mới → clear thoughts cũ trước khi nhận thoughts mới.
      // Khi assistant message → giữ thoughts để hiển thị inline với reply.
      const clearThoughts = msg.role === "user";
      return {
        messages: [...s.messages, msg],
        thoughts: clearThoughts ? [] : s.thoughts,
      };
    }),
  appendDelta: (messageId, delta) =>
    set((s) => {
      const idx = s.messages.findIndex((m) => m.id === messageId);
      if (idx === -1) return s;
      const target = s.messages[idx];
      if (!target || target.content.type !== "text") return s;
      const next = [...s.messages];
      next[idx] = {
        ...target,
        content: { type: "text", text: target.content.text + delta },
      };
      return { messages: next };
    }),
  // Không clear thoughts — chỉ mark done. User thấy được lịch sử suy nghĩ
  // sau khi reply xong. Clear chỉ xảy ra khi: (1) user gửi message mới
  // (xem appendMessage), (2) đổi conversation (xem setActiveConversation).
  finishMessage: () =>
    set((s) => ({
      thoughts: s.thoughts.map((t) =>
        t.status === "running"
          ? { ...t, status: "done", endedAt: Date.now() }
          : t,
      ),
    })),
  addThought: (thought) =>
    set((s) => {
      // Mark thought running trước đó là done (vì 1 thought mới đến nghĩa
      // là step trước đã hoàn thành).
      const updatedPrev = s.thoughts.map((t) =>
        t.status === "running"
          ? { ...t, status: "done" as const, endedAt: Date.now() }
          : t,
      );
      return { thoughts: [...updatedPrev, thought] };
    }),
  finishThought: (id) =>
    set((s) => ({
      thoughts: s.thoughts.map((t) =>
        t.id === id ? { ...t, status: "done", endedAt: Date.now() } : t,
      ),
    })),
  setConnectionStatus: (status) => set({ connectionStatus: status }),
  addWidget: (widget) =>
    set((s) => {
      const next = new Map(s.widgets);
      next.set(widget.widget_id, widget);
      return { widgets: next };
    }),
  updateWidgetStatus: (widget_id, status) =>
    set((s) => {
      const next = new Map(s.widgets);
      const w = next.get(widget_id);
      if (w) next.set(widget_id, { ...w, status });
      return { widgets: next };
    }),
  setLLM: (provider, model) => set({ selectedProvider: provider, selectedModel: model }),
  setComposerDraft: (text) => set({ composerDraft: text }),
  reset: () => set(initialState),
}));
